import type { ApolloClient } from '@apollo/client';
import { useAuthChange } from '@saleor/auth-sdk/react';
import { getCurrentScope } from '@sentry/nextjs';
import { useContext, useEffect, useRef, useState } from 'react';
import type { Maybe } from 'yup';

import {
  APOLLO_CLIENTS,
  CustomContext,
  initializeApollo,
} from '@/graphql/client';
import type {
  ConfigurationActivationConfig,
  ConfigurationGetCheckoutConfigsQuery,
  ConfigurationGetCheckoutConfigsQueryVariables,
  ConfigurationGetModuleActivationsQuery,
  ConfigurationGetModuleActivationsQueryVariables,
  ConfigurationGetWarrantyExtensionCampaignsQuery,
  ConfigurationGetWarrantyExtensionCampaignsQueryVariables,
} from '@/graphql/configuration/api';
import {
  GetCheckoutConfigsDocument,
  GetModuleActivationsDocument,
  GetWarrantyExtensionCampaignsDocument,
} from '@/graphql/configuration/api';
import type {
  AddressValidationRulesQuery,
  AddressValidationRulesQueryVariables,
  CheckoutLanguageCodeUpdateMutation,
  CheckoutLanguageCodeUpdateMutationVariables,
  CountryCode,
} from '@/graphql/saleor/api';
import {
  AddressValidationRulesDocument,
  CheckoutLanguageCodeUpdateDocument,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useOnRouteChange } from '@/hooks/useOnRouteChange';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { getVoucherType } from '@/lib/checkout';
import { getBrandConfig } from '@/lib/configuration/configuration';
import {
  API_URI,
  GLOBAL_E_MARKET_AVAILABLE,
  MOCK_DEFAULT_EMAIL,
} from '@/lib/const';
import {
  getEcsConfigurationByCountry,
  getEcsConfigurations,
} from '@/lib/service/helpers';
import type { EcsConfig } from '@/lib/service/type';
import { StorageHelper } from '@/lib/storage';
import type { WithChildren } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions } from '@/states/checkout';
import { userActions, userState } from '@/states/user';

import { LocaleContext } from '../LocaleProvider';
import type { ConfigProviderContext } from './ConfigContext';
import { Provider } from './ConfigContext';
import {
  checkIsOrderPaid,
  getCheckoutOrders,
  getUserCheckout,
} from './helpers';

type InternalContext = Pick<ConfigProviderContext, 'headerRef'> & {
  affirmPaymentMax?: number;
  agosPaymentMax: number;
  agosPaymentMin: number;
  applepayExpressPaymentMax?: number | null;
  applepayExpressPaymentMin?: number | null;
  cembraFinancingPaymentMax: number;
  cembraFinancingPaymentMin: number;
  cembraOpenInvoicePaymentMax: number;
  cembraOpenInvoicePaymentMin: number;
  creditPlusPaymentMax: number;
  creditPlusPaymentMin: number;
  ecsConfig: Maybe<EcsConfig>;
  ecsConfigurations: EcsConfig[];
  moduleActivations: ConfigurationActivationConfig[] | null;
  oney3xPaymentMax: number;
  oney3xPaymentMin: number;
  paypalExpressPaymentMax?: number | null;
  paypalExpressPaymentMin?: number | null;
  restoreCheckoutInitialized: boolean;
  v12PaymentMax?: number;
  v12PaymentMin?: number;
  validationRules: Partial<
    Record<CountryCode, ReturnType<ConfigProviderContext['getValidationRules']>>
  >;
  warrantyExtensionCampaignEnable: boolean;
  warrantyExtensionCampaignEndDate: string;
  warrantyExtensionCampaignStartDate: string;
  zipPaymentMax: number;
  zipPaymentMin: number;
};

interface ConfigProviderProps extends WithChildren {
  apolloClient: ApolloClient<unknown>;
}

export enum Modules {
  BREITLING_TRADE = 'BREITLING_TRADE',
  FOR_GIFT = 'FOR_GIFT',
  GIFT_WITH_PURCHASE = 'GIFT_WITH_PURCHASE',
  ENGRAVING = 'ENGRAVING',
  E_COMMERCE = 'E_COMMERCE',
  ECS = 'ECS',
  RISKFIED_MODE = 'RISKFIED_MODE',
  BOUTIQUE_PICKUP = 'BOUTIQUE_PICKUP',
  AFFIRM = 'AFFIRM',
  CPI_CHECK = 'CPI_CHECK',
  CEMBRA_FINANCING = 'CEMBRA_FINANCING',
  PDP_CEMBRA_FINANCING = 'PDP_CEMBRA_FINANCING',
  CEMBRA_OPEN_INVOICE = 'CEMBRA_OPEN_INVOICE',
  PDP_ONEY_3X = 'PDP_ONEY_3X',
  PDP_AGOS = 'PDP_AGOS',
  PDP_CREDIT_PLUS = 'PDP_CREDIT_PLUS',
  SECURE_PAYMENT = 'SECURE_PAYMENT',
  GIFT_SERVICE = 'GIFT_SERVICE',
  FREE_RETURN = 'FREE_RETURN',
  FREE_SHIPPING = 'FREE_SHIPPING',
  PERSONAL_ADVISOR = 'PERSONAL_ADVISOR',
  ZIP = 'ZIP',
  AVAILABILITY_REQUEST = 'AVAILABILITY_REQUEST',
}

export const isModuleActive = (
  moduleActivations: ConfigurationActivationConfig[] | null,
  moduleType: Modules,
) => {
  return moduleActivations?.find((e) => e.module === moduleType)?.active;
};

export const ConfigProvider = ({
  apolloClient,
  children,
}: ConfigProviderProps) => {
  const {
    create: createCheckout,
    customerAttach,
    emailUpdate,
    endProcessing,
    restore: restoreCheckout,
    snapshot: { checkout, notification, orderId },
  } = useCheckout();
  const locale = useContext(LocaleContext);
  const { fetchUser } = useUser();
  const [{ validationRules, ...ctx }, setCtx] = useState<InternalContext>({
    ecsConfigurations: [],
    ecsConfig: null,
    headerRef: null,
    validationRules: {},
    warrantyExtensionCampaignEnable: false,
    warrantyExtensionCampaignEndDate: '',
    warrantyExtensionCampaignStartDate: '',
    moduleActivations: null,
    affirmPaymentMax: undefined,
    cembraFinancingPaymentMax: 0,
    cembraFinancingPaymentMin: 0,
    cembraOpenInvoicePaymentMax: 0,
    cembraOpenInvoicePaymentMin: 0,
    oney3xPaymentMax: 0,
    oney3xPaymentMin: 0,
    paypalExpressPaymentMax: null,
    paypalExpressPaymentMin: null,
    applepayExpressPaymentMax: null,
    applepayExpressPaymentMin: null,
    agosPaymentMax: 0,
    agosPaymentMin: 0,
    creditPlusPaymentMax: 0,
    creditPlusPaymentMin: 0,
    restoreCheckoutInitialized: false,
    zipPaymentMax: 0,
    zipPaymentMin: 0,
  });
  const {
    country,
    productCatalogMode,
    query: { languageCode },
  } = useRegionProvider();
  const { push, query } = useRouter();

  const [shouldRestoreCheckout, setShouldRestoreCheckout] = useState(false);
  const client = initializeApollo();

  const setHeaderRef: ConfigProviderContext['setHeaderRef'] = (ref) =>
    setCtx((ctx) => ({ ...ctx, headerRef: ref }));

  const getValidationRules: ConfigProviderContext['getValidationRules'] =
    async (country) => {
      if (country in validationRules) {
        return validationRules[country] as ReturnType<
          ConfigProviderContext['getValidationRules']
        >;
      }

      const { data } = await client.query<
        AddressValidationRulesQuery,
        AddressValidationRulesQueryVariables
      >({
        fetchPolicy: 'cache-first',
        variables: { countryCode: country },
        query: AddressValidationRulesDocument,
      });

      const rule = data?.addressValidationRules || null;

      setCtx((ctx) => ({
        ...ctx,
        validationRules: {
          ...ctx.validationRules,
          [country]: rule,
        },
      }));

      return rule;
    };

  const getWarrantyExtensionCampaignStatus: ConfigProviderContext['getWarrantyExtensionCampaignStatus'] =
    async (country) => {
      const { data } = await client.query<
        ConfigurationGetWarrantyExtensionCampaignsQuery,
        ConfigurationGetWarrantyExtensionCampaignsQueryVariables
      >({
        fetchPolicy: 'cache-first',
        variables: { countries: [country] },
        query: GetWarrantyExtensionCampaignsDocument,
        context: {
          client: APOLLO_CLIENTS.configurationApp,
        },
      });

      let isEnable = false;
      if (data?.warrantyExtensionCampaigns?.length) {
        isEnable = data?.warrantyExtensionCampaigns[0]?.enable ?? false;
        const warrantyExtensionCampaignEndDate =
          data?.warrantyExtensionCampaigns[0]?.endDate;
        const warrantyExtensionCampaignStartDate =
          data?.warrantyExtensionCampaigns[0]?.startDate;

        setCtx((ctx) => ({
          ...ctx,
          warrantyExtensionCampaignEnable: isEnable,
          warrantyExtensionCampaignEndDate,
          warrantyExtensionCampaignStartDate,
        }));
      }

      return isEnable;
    };

  const getModuleActivations: ConfigProviderContext['getModuleActivations'] =
    async (country, modules, refresh = false) => {
      const { data } = await client.query<
        ConfigurationGetModuleActivationsQuery,
        ConfigurationGetModuleActivationsQueryVariables
      >({
        fetchPolicy: 'cache-first',
        variables: { countries: [country], modules },
        query: GetModuleActivationsDocument,
        context: {
          client: APOLLO_CLIENTS.configurationApp,
        },
      });
      // refresh default setting is false. Otherwise, when calling from other places, the passed module is missing, which will cause bugs.
      if (data?.moduleActivations?.length && refresh) {
        setCtx((ctx) => ({
          ...ctx,
          moduleActivations: data?.moduleActivations?.[0]?.configs || [],
        }));
      }

      return data;
    };

  const getCheckoutConfigs: ConfigProviderContext['getCheckoutConfigs'] =
    async (country) => {
      const { data } = await client.query<
        ConfigurationGetCheckoutConfigsQuery,
        ConfigurationGetCheckoutConfigsQueryVariables
      >({
        fetchPolicy: 'cache-first',
        variables: { countries: [country] },
        query: GetCheckoutConfigsDocument,
        context: {
          client: APOLLO_CLIENTS.configurationApp,
        },
      });

      if (data?.checkoutConfigs?.length) {
        setCtx((ctx) => ({
          ...ctx,
          checkoutConfigs: data?.checkoutConfigs || [],
          affirmPaymentMax: data?.checkoutConfigs?.[0]?.affirmPaymentMax || 0,
          klarnaPayNowPaymentMax:
            data?.checkoutConfigs?.[0]?.klarnaPayNowPaymentMax || 0,
          twintPaymentMax: data?.checkoutConfigs?.[0]?.twintPaymentMax || 0,
          cembraFinancingPaymentMax:
            data?.checkoutConfigs?.[0]?.cembraFinancingPaymentMax || 0,
          cembraFinancingPaymentMin:
            data?.checkoutConfigs?.[0]?.cembraFinancingPaymentMin || 0,
          cembraOpenInvoicePaymentMax:
            data?.checkoutConfigs?.[0]?.cembraOpenInvoicePaymentMax || 0,
          cembraOpenInvoicePaymentMin:
            data?.checkoutConfigs?.[0]?.cembraOpenInvoicePaymentMin || 0,
          oney3xPaymentMax: data?.checkoutConfigs?.[0]?.oney3xPaymentMax || 0,
          oney3xPaymentMin: data?.checkoutConfigs?.[0]?.oney3xPaymentMin || 0,
          paypalExpressPaymentMax:
            data?.checkoutConfigs?.[0]?.paypalExpressPaymentMax || null,
          paypalExpressPaymentMin:
            data?.checkoutConfigs?.[0]?.paypalExpressPaymentMin || null,
          applepayExpressPaymentMax:
            data?.checkoutConfigs?.[0]?.applepayExpressPaymentMax || null,
          applepayExpressPaymentMin:
            data?.checkoutConfigs?.[0]?.applepayExpressPaymentMin || null,
          v12PaymentMax: data?.checkoutConfigs?.[0]?.v12PaymentMax || 0,
          v12PaymentMin: data?.checkoutConfigs?.[0]?.v12PaymentMin || 0,
          agosPaymentMax: data?.checkoutConfigs?.[0]?.agosPaymentMax || 0,
          agosPaymentMin: data?.checkoutConfigs?.[0]?.agosPaymentMin || 0,
          creditPlusPaymentMax:
            data?.checkoutConfigs?.[0]?.creditPlusPaymentMax || 0,
          creditPlusPaymentMin:
            data?.checkoutConfigs?.[0]?.creditPlusPaymentMin || 0,
          zipPaymentMax: data?.checkoutConfigs?.[0]?.zipPaymentMax || 0,
          zipPaymentMin: data?.checkoutConfigs?.[0]?.zipPaymentMin || 0,
        }));
      }

      return data;
    };

  const getEcsConfig = async (country: CountryCode) => {
    setCtx((ctx) => ({
      ...ctx,
      ecsConfig: getEcsConfigurationByCountry(country),
      ecsConfigurations: getEcsConfigurations(),
    }));
  };

  /**
   * Clear add to bag notification if a route was changed.
   */
  useOnRouteChange(() => {
    if (notification) {
      checkoutActions.notify(null);
    }
  });

  useEffect(() => {
    if (checkout?.voucherCode && checkout?.channel?.slug) {
      const fetchVoucherType = async () => {
        const data = await getVoucherType({
          code: checkout.voucherCode ?? '',
          channel: checkout.channel?.slug ?? '',
        });
        checkoutActions.setVoucherType(data.voucher?.discountValueType ?? '');
      };
      fetchVoucherType();
    } else {
      checkoutActions.setVoucherType('');
    }
  }, [checkout?.voucherCode, checkout?.channel?.slug]);

  const loading = useRef(false);

  const router = useRouter();

  /**
   * Clean checkout
   * When do you need to clear the shopping cart?
   * 1. Switch country
   * or
   * 2. The order has been paid successfully
   */
  useEffect(() => {
    const cleanCheckout = async () => {
      if (loading.current) return;
      loading.current = true;

      try {
        let shouldClearCheckout = false;

        if (
          checkout?.channel?.slug &&
          checkout?.channel?.slug !== country?.channelSlug
        ) {
          shouldClearCheckout = true;
        }

        if (orderId && checkIsOrderPaid) {
          const isOrderPaid = await checkIsOrderPaid(orderId, languageCode);
          if (isOrderPaid) {
            shouldClearCheckout = true;
          }
        }

        if (shouldClearCheckout) {
          await checkoutActions.clear();
        }
      } finally {
        loading.current = false;
      }
    };

    const handleRouteChange = () => {
      cleanCheckout();
    };

    const isExternalRedirect =
      typeof performance !== 'undefined' &&
      performance.getEntriesByType &&
      performance.getEntriesByType('navigation')[0] &&
      (
        performance.getEntriesByType(
          'navigation',
        )[0] as PerformanceNavigationTiming
      ).type === 'navigate';

    if (isExternalRedirect) {
      handleRouteChange();
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [
    router,
    orderId,
    checkout?.channel,
    country?.channelSlug,
    checkoutActions,
    checkIsOrderPaid,
    languageCode,
  ]);

  /**
   * Initial checkout restore.
   * When do you need to initial checkout restore?
   * first time open the website and after sign in
   */
  useEffect(() => {
    setShouldRestoreCheckout(true);
  }, []);

  useEffect(() => {
    if (!shouldRestoreCheckout) return;

    const restore = async () => {
      if (
        getBrandConfig().disableAlgoliaApiCall ||
        loading.current ||
        productCatalogMode
      ) {
        return;
      }
      setCtx((ctx) => ({
        ...ctx,
        restoreCheckoutInitialized: false,
      }));
      await handleCheckout();
      setCtx((ctx) => ({
        ...ctx,
        restoreCheckoutInitialized: true,
      }));
      endProcessing({ initializing: false });
      setShouldRestoreCheckout(false);
    };
    restore();
  }, [shouldRestoreCheckout]);

  // Handle checkout to retore the one linked to the user or create a new one if not found or the channel changed.
  const handleCheckout = async () => {
    const user = await fetchUser();
    if (!user) {
      await restoreCheckout();
      return;
    }

    // Edge case for Global-e: when the order is confirmed on Global-e, there is a timing issue to create the order on our side.
    // We create a new checkout if the local storage is set because it means that the order is confirmed on Global-e side.
    if (GLOBAL_E_MARKET_AVAILABLE?.includes(locale)) {
      const storageHelper = new StorageHelper();
      if (storageHelper.getLocalStorage('GLobalEOrderConfirmed')) {
        storageHelper.removeLocalStorage('GLobalEOrderConfirmed');
        await restoreCheckout();
        return;
      }
    }

    const userCheckout = await getUserCheckout(
      user.email ?? '',
      country.channelSlug ?? '',
    );
    const checkoutOrders = userCheckout?.id
      ? await getCheckoutOrders(userCheckout.id)
      : undefined;

    const checkoutPaidAmount =
      checkoutOrders?.reduce((v, order) => {
        const paidAmount =
          (order.node.totalAuthorized?.amount || 0) +
          (order.node.totalCharged?.amount || 0);
        return v + paidAmount;
      }, 0) ?? 0;

    const hasNonEmptyGuestCheckout =
      Boolean(checkout?.lines?.length) &&
      (!userCheckout || checkout?.id !== userCheckout?.id);

    const isValidUserCheckout =
      Boolean(userCheckout?.id) &&
      userCheckout?.channel?.slug === country.channelSlug;

    const performCheckoutCreation = async () => {
      const newCheckout = await createCheckout(true);
      if (newCheckout && 'id' in newCheckout && !newCheckout.user) {
        await customerAttach(newCheckout.id);
      }
    };

    if (checkoutPaidAmount > 0) {
      await performCheckoutCreation();
    } else if (hasNonEmptyGuestCheckout) {
      if (checkout?.email === MOCK_DEFAULT_EMAIL && user.email) {
        await emailUpdate(user.email);
      }
      await customerAttach();
    } else if (isValidUserCheckout) {
      await restoreCheckout(userCheckout.id);
    } else {
      await performCheckoutCreation();
    }
  };

  /**
   * update checkoutLanguageCode when switch language
   */
  useEffect(() => {
    const updateCheckoutLanguage = async () => {
      if (checkout?.id && languageCode !== checkout?.languageCode) {
        const client = initializeApollo();

        await client.mutate<
          CheckoutLanguageCodeUpdateMutation,
          CheckoutLanguageCodeUpdateMutationVariables
        >({
          mutation: CheckoutLanguageCodeUpdateDocument,
          variables: {
            id: checkout.id,
            languageCode,
          },
          context: CustomContext({
            disableErrorNotification: true,
          }),
        });
      }
    };
    updateCheckoutLanguage();
  }, [checkout?.id, languageCode]);

  /**
   * refetch data when switch country
   */
  useEffect(() => {
    (async () => {
      if (getBrandConfig().disableAlgoliaApiCall) return;
      getWarrantyExtensionCampaignStatus(country.code);
      getModuleActivations(
        country.code,
        [
          Modules.BOUTIQUE_PICKUP,
          Modules.AFFIRM,
          Modules.FOR_GIFT,
          Modules.PDP_CEMBRA_FINANCING,
          Modules.PDP_ONEY_3X,
          Modules.PDP_AGOS,
          Modules.PDP_CREDIT_PLUS,
          Modules.ENGRAVING,
          Modules.SECURE_PAYMENT,
          Modules.GIFT_SERVICE,
          Modules.FREE_RETURN,
          Modules.FREE_SHIPPING,
          Modules.PERSONAL_ADVISOR,
          Modules.ZIP,
          Modules.AVAILABILITY_REQUEST,
        ],
        true,
      );
      getCheckoutConfigs(country.code);
      getEcsConfig(country.code);
    })();
  }, [country.code]);

  /**
   * SDK auth hooks.
   */
  useAuthChange({
    saleorApiUrl: API_URI,
    onSignedOut: async () => {
      const isAuthenticated = userState.isAuthenticated;
      // Wait for all ongoing queries to complete
      await apolloClient.stop();
      await Promise.all([apolloClient.resetStore(), userActions.clear()]);
      // login failed will trigger this event, if is login failed ,not reset checkout
      if (isAuthenticated) {
        await checkoutActions.clear();
        await restoreCheckout();
      }
      getCurrentScope().setUser(null);
    },
    onSignedIn: async () => {
      try {
        await apolloClient.refetchQueries({ include: 'active' });
        const user = await fetchUser();

        if (!user) {
          console.error('No user data received after sign in');
          return;
        }
        setShouldRestoreCheckout(true);

        if (query?.next) {
          await push(query.next as string);
        }
      } catch (error) {
        console.error('Error in onSignedIn callback:', error);
      }
    },
  });

  return (
    <Provider
      value={{
        ...ctx,
        getValidationRules,
        getWarrantyExtensionCampaignStatus,
        getModuleActivations,
        getCheckoutConfigs,
        setHeaderRef,
      }}
    >
      {children}
    </Provider>
  );
};
